/**
 * Tenant Admin API Service
 * Handles tenant-scoped admin operations for tenant_admin users
 */

import { apiGet, apiPatch, apiPost, apiDelete } from "./api";
import type {
  TenantStats,
  TenantSettingsResponse,
  TenantSettingsUpdate,
  TenantGuide,
  TenantGuideListItem,
  TenantGuideCreate,
  TenantGuideUpdate,
  TenantLibraryResource,
  TenantLibraryResourceListItem,
  TenantLibraryResourceCreate,
  TenantLibraryResourceUpdate,
  TenantAgentTemplate,
  TenantAgentTemplateListItem,
  TenantAgentTemplateCreate,
  TenantAgentTemplateUpdate,
  ApiTenantStats,
  ApiTenantSettingsResponse,
  ApiTenantGuide,
  ApiTenantGuideListItem,
  ApiTenantLibraryResource,
  ApiTenantLibraryResourceListItem,
  ApiTenantAgentTemplate,
  ApiTenantAgentTemplateListItem,
} from "@/types/tenantAdmin";
import type {
  GuideIcon,
  LibraryResourceType,
  LibraryResourceSource,
  LibraryDifficulty,
  TemplateCategory,
  TemplateDifficulty,
} from "@/types/models";

// ============================================================================
// Response Transformers
// ============================================================================

function transformTenantStats(api: ApiTenantStats): TenantStats {
  return {
    tenantId: api.tenant_id,
    tenantName: api.tenant_name,
    users: {
      totalUsers: api.users.total_users,
      activeUsers: api.users.active_users,
      byRole: api.users.by_role,
    },
    privateContent: {
      guides: api.private_content.guides,
      libraryResources: api.private_content.library_resources,
      agentTemplates: api.private_content.agent_templates,
    },
    workshops: {
      totalWorkshops: api.workshops.total_workshops,
      activeWorkshops: api.workshops.active_workshops,
      totalExercises: api.workshops.total_exercises,
    },
    generatedAt: api.generated_at,
  };
}

function transformTenantSettings(
  api: ApiTenantSettingsResponse,
): TenantSettingsResponse {
  return {
    tenantId: api.tenant_id,
    tenantName: api.tenant_name,
    settings: {
      branding: {
        logoUrl: api.settings.branding.logo_url,
        primaryColor: api.settings.branding.primary_color,
        secondaryColor: api.settings.branding.secondary_color,
      },
      features: {
        enableAgentBuilder: api.settings.features.enable_agent_builder,
        enableTemplates: api.settings.features.enable_templates,
        enableLibrary: api.settings.features.enable_library,
        maxWorkshops: api.settings.features.max_workshops,
        maxUsers: api.settings.features.max_users,
      },
      security: {
        requireMfa: api.settings.security.require_mfa,
        allowedEmailDomains: api.settings.security.allowed_email_domains,
        sessionTimeoutMinutes: api.settings.security.session_timeout_minutes,
      },
    },
    updatedAt: api.updated_at,
  };
}

function transformTenantGuide(api: ApiTenantGuide): TenantGuide {
  return {
    id: api.id,
    tenantId: api.tenant_id,
    slug: api.slug,
    title: api.title,
    description: api.description,
    contentHtml: api.content_html,
    icon: api.icon as GuideIcon,
    displayOrder: api.display_order,
    published: api.published,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

function transformTenantGuideListItem(
  api: ApiTenantGuideListItem,
): TenantGuideListItem {
  return {
    id: api.id,
    tenantId: api.tenant_id,
    slug: api.slug,
    title: api.title,
    description: api.description,
    icon: api.icon as GuideIcon,
    displayOrder: api.display_order,
    published: api.published,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

function transformTenantLibraryResource(
  api: ApiTenantLibraryResource,
): TenantLibraryResource {
  return {
    id: api.id,
    tenantId: api.tenant_id,
    title: api.title,
    description: api.description,
    resourceType: api.resource_type as LibraryResourceType,
    source: api.source as LibraryResourceSource,
    externalUrl: api.external_url,
    contentPath: api.content_path,
    contentHtml: api.content_html,
    topics: api.topics,
    difficulty: api.difficulty as LibraryDifficulty,
    author: api.author,
    estimatedMinutes: api.estimated_minutes,
    thumbnailUrl: api.thumbnail_url,
    featured: api.featured,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

function transformTenantLibraryResourceListItem(
  api: ApiTenantLibraryResourceListItem,
): TenantLibraryResourceListItem {
  return {
    id: api.id,
    tenantId: api.tenant_id,
    title: api.title,
    description: api.description,
    resourceType: api.resource_type as LibraryResourceType,
    source: api.source as LibraryResourceSource,
    topics: api.topics,
    difficulty: api.difficulty as LibraryDifficulty,
    author: api.author,
    estimatedMinutes: api.estimated_minutes,
    featured: api.featured,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

function transformTenantAgentTemplate(
  api: ApiTenantAgentTemplate,
): TenantAgentTemplate {
  return {
    id: api.id,
    tenantId: api.tenant_id,
    name: api.name,
    description: api.description,
    yamlContent: api.yaml_content,
    category: api.category as TemplateCategory,
    difficulty: api.difficulty as TemplateDifficulty,
    useCase: api.use_case,
    tags: api.tags,
    authorId: api.author_id,
    authorName: api.author_name,
    featured: api.featured,
    downloadCount: api.download_count,
    model: api.model,
    hasTools: api.has_tools,
    hasSubAgents: api.has_sub_agents,
    thumbnailUrl: api.thumbnail_url,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

function transformTenantAgentTemplateListItem(
  api: ApiTenantAgentTemplateListItem,
): TenantAgentTemplateListItem {
  return {
    id: api.id,
    tenantId: api.tenant_id,
    name: api.name,
    description: api.description,
    category: api.category as TemplateCategory,
    difficulty: api.difficulty as TemplateDifficulty,
    tags: api.tags,
    authorName: api.author_name,
    featured: api.featured,
    downloadCount: api.download_count,
    model: api.model,
    hasTools: api.has_tools,
    hasSubAgents: api.has_sub_agents,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

// ============================================================================
// Stats API
// ============================================================================

export async function getTenantStats(): Promise<TenantStats> {
  const response = await apiGet<ApiTenantStats>("/tenant-admin/stats");
  return transformTenantStats(response);
}

// ============================================================================
// Settings API
// ============================================================================

export async function getTenantSettings(): Promise<TenantSettingsResponse> {
  const response = await apiGet<ApiTenantSettingsResponse>(
    "/tenant-admin/settings",
  );
  return transformTenantSettings(response);
}

export async function updateTenantSettings(
  data: TenantSettingsUpdate,
): Promise<TenantSettingsResponse> {
  // Transform camelCase to snake_case for API
  const apiData: Record<string, unknown> = {};

  if (data.branding) {
    apiData.branding = {
      logo_url: data.branding.logoUrl,
      primary_color: data.branding.primaryColor,
      secondary_color: data.branding.secondaryColor,
    };
  }

  if (data.features) {
    apiData.features = {
      enable_agent_builder: data.features.enableAgentBuilder,
      enable_templates: data.features.enableTemplates,
      enable_library: data.features.enableLibrary,
      max_workshops: data.features.maxWorkshops,
      max_users: data.features.maxUsers,
    };
  }

  if (data.security) {
    apiData.security = {
      require_mfa: data.security.requireMfa,
      allowed_email_domains: data.security.allowedEmailDomains,
      session_timeout_minutes: data.security.sessionTimeoutMinutes,
    };
  }

  const response = await apiPatch<ApiTenantSettingsResponse>(
    "/tenant-admin/settings",
    apiData,
  );
  return transformTenantSettings(response);
}

// ============================================================================
// Guides API
// ============================================================================

export async function getTenantGuides(): Promise<TenantGuideListItem[]> {
  const response = await apiGet<ApiTenantGuideListItem[]>(
    "/tenant-admin/guides",
  );
  return response.map(transformTenantGuideListItem);
}

export async function getTenantGuide(slug: string): Promise<TenantGuide> {
  const response = await apiGet<ApiTenantGuide>(`/tenant-admin/guides/${slug}`);
  return transformTenantGuide(response);
}

export async function createTenantGuide(
  data: TenantGuideCreate,
): Promise<TenantGuide> {
  const response = await apiPost<ApiTenantGuide>("/tenant-admin/guides", data);
  return transformTenantGuide(response);
}

export async function updateTenantGuide(
  slug: string,
  data: TenantGuideUpdate,
): Promise<TenantGuide> {
  const response = await apiPatch<ApiTenantGuide>(
    `/tenant-admin/guides/${slug}`,
    data,
  );
  return transformTenantGuide(response);
}

export async function deleteTenantGuide(slug: string): Promise<void> {
  await apiDelete<void>(`/tenant-admin/guides/${slug}`);
}

// ============================================================================
// Library Resources API
// ============================================================================

export async function getTenantLibraryResources(): Promise<
  TenantLibraryResourceListItem[]
> {
  const response = await apiGet<ApiTenantLibraryResourceListItem[]>(
    "/tenant-admin/library",
  );
  return response.map(transformTenantLibraryResourceListItem);
}

export async function getTenantLibraryResource(
  id: string,
): Promise<TenantLibraryResource> {
  const response = await apiGet<ApiTenantLibraryResource>(
    `/tenant-admin/library/${id}`,
  );
  return transformTenantLibraryResource(response);
}

export async function createTenantLibraryResource(
  data: TenantLibraryResourceCreate,
): Promise<TenantLibraryResource> {
  const response = await apiPost<ApiTenantLibraryResource>(
    "/tenant-admin/library",
    data,
  );
  return transformTenantLibraryResource(response);
}

export async function updateTenantLibraryResource(
  id: string,
  data: TenantLibraryResourceUpdate,
): Promise<TenantLibraryResource> {
  const response = await apiPatch<ApiTenantLibraryResource>(
    `/tenant-admin/library/${id}`,
    data,
  );
  return transformTenantLibraryResource(response);
}

export async function deleteTenantLibraryResource(id: string): Promise<void> {
  await apiDelete<void>(`/tenant-admin/library/${id}`);
}

// ============================================================================
// Agent Templates API
// ============================================================================

export async function getTenantAgentTemplates(): Promise<
  TenantAgentTemplateListItem[]
> {
  const response = await apiGet<ApiTenantAgentTemplateListItem[]>(
    "/tenant-admin/templates",
  );
  return response.map(transformTenantAgentTemplateListItem);
}

export async function getTenantAgentTemplate(
  id: string,
): Promise<TenantAgentTemplate> {
  const response = await apiGet<ApiTenantAgentTemplate>(
    `/tenant-admin/templates/${id}`,
  );
  return transformTenantAgentTemplate(response);
}

export async function createTenantAgentTemplate(
  data: TenantAgentTemplateCreate,
): Promise<TenantAgentTemplate> {
  const response = await apiPost<ApiTenantAgentTemplate>(
    "/tenant-admin/templates",
    data,
  );
  return transformTenantAgentTemplate(response);
}

export async function updateTenantAgentTemplate(
  id: string,
  data: TenantAgentTemplateUpdate,
): Promise<TenantAgentTemplate> {
  const response = await apiPatch<ApiTenantAgentTemplate>(
    `/tenant-admin/templates/${id}`,
    data,
  );
  return transformTenantAgentTemplate(response);
}

export async function deleteTenantAgentTemplate(id: string): Promise<void> {
  await apiDelete<void>(`/tenant-admin/templates/${id}`);
}
